import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CommonService } from '../../../vass-core/services/common.service';

import { environment } from '../../../../environments/environment';

import { SelectModel } from '../../../mortgage/models/select.model';
import { PreSimulatorModel } from '../../../shared/models/pre-simulator.model';
import { CCAAModel } from '../../models/ccaa.model';
import { ConfigInputPreSimulatorModel } from '../../models/config-input-pre-simulator.model';
import { CountryModel } from '../../models/country.model';

import { CustomHttpParams } from '../../../vass-core/services/custom-http-params.class';
import { IS_PUBLIC_API } from '../../../vass-core/utils/is-public-api- context';
import { MortgageSimulateResponse } from '../../models/api/models';

@Injectable()
export class SimulatorService {
  private onResetForm = new Subject<boolean>();

  private preSimuate: PreSimulatorModel;
  public ccaa: Array<CCAAModel> = [];
  public MORTGAGE_AMOUNT_MIN: number = 0;
  private countries$: BehaviorSubject<CountryModel[]> = new BehaviorSubject(undefined);

  constructor(
    private commonService: CommonService,
    private readonly http: HttpClient,
  ) {
    this.getCountriesRequest();
  }

  getCCAA(): Observable<Array<CCAAModel>> {
    const url = `${environment.apiBase}/mortgages-config-params/${environment.api_version.config_params}/location/regions`;
    return this.http
      .get<Array<CCAAModel>>(url, {
        params: new CustomHttpParams(true),
        context: new HttpContext().set(IS_PUBLIC_API, true),
      })
      .pipe(
        map((regions) => {
          const index = regions.findIndex((e) => e.id === '999999999');
          if (index > -1) {
            regions.splice(index, 1);
          }
          return regions;
        }),
      );
  }

  simular(body: any) {
    const url = `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/simulate/level1`;
    return this.http.post<MortgageSimulateResponse>(url, body, {
      context: new HttpContext().set(IS_PUBLIC_API, true),
    });
  }

  getConfigInputs(): Observable<ConfigInputPreSimulatorModel> {
    const url = `${environment.apiBase}/mortgages-config-params/${environment.api_version.config_params}/config?type=PRE_SIMULATION`;
    return forkJoin(
      this.http.get<any>(`./assets/mocks/config.mock.json`),
      this.http
        .get<Array<SelectModel>>(url, {
          params: new CustomHttpParams(true),
          context: new HttpContext().set(IS_PUBLIC_API, true),
        })
        .pipe(catchError((e) => of({}))),
    ).pipe(
      map(([settings, respService]) => {
        for (const item in respService) {
          switch (respService[item].code) {
            case 'MONTHLY_INCOME_MAX': {
              settings.monthlyIncome.max = parseInt(respService[item].value, 10);
              break;
            }
            case 'MONTHLY_INCOME_MIN': {
              settings.monthlyIncome.min = parseInt(respService[item].value, 10);
              break;
            }
            case 'MORTGAGE_AMOUNT_MAX': {
              settings.mortgageAmount.max = parseInt(respService[item].value, 10);
              break;
            }
            case 'MORTGAGE_AMOUNT_MIN': {
              settings.mortgageAmount.min = parseInt(respService[item].value, 10);
              this.MORTGAGE_AMOUNT_MIN = parseInt(respService[item].value, 10);
              break;
            }
            case 'PROPERTY_PRICE_MAX': {
              settings.propertyPrice.max = parseInt(respService[item].value, 10);
              break;
            }
            case 'PROPERTY_PRICE_MIN': {
              settings.propertyPrice.min = parseInt(respService[item].value, 10);
              break;
            }
            case 'TERM_IN_YEARS_MAX': {
              settings.termInYears.max = parseInt(respService[item].value, 10);
              break;
            }
            case 'TERM_IN_YEARS_MIN': {
              settings.termInYears.min = parseInt(respService[item].value, 10);
              break;
            }
            case 'MAX_PERCENTAGE_FINANCING': {
              settings.maxFinancing = parseFloat(respService[item].value);
              break;
            }
          }
        }
        settings.maxFinancing = settings.maxFinancing ? settings.maxFinancing : 0.9;
        return settings;
      }),
    );
  }

  getOnResetForm(): Observable<any> {
    return this.onResetForm.asObservable();
  }

  resetForm() {
    this.onResetForm.next(true);
  }

  setPreSimulator(simulate: PreSimulatorModel) {
    this.preSimuate = simulate;
  }

  getPreSimulator() {
    return this.preSimuate;
  }

  getDocumentType() {
    const url = `${environment.apiBase}/toolbox/${environment.api_version.toolbox}/documentType`;
    return this.commonService.getVerb(url);
  }

  getMaritalStatus() {
    const url = `${environment.apiBase}/toolbox/${environment.api_version.toolbox}/civilStatus`;
    return this.commonService.getVerb(url);
  }

  getEmploymentSituation() {
    const url = `${environment.apiBase}/toolbox/${environment.api_version.toolbox}/laboral/situation`;
    return this.commonService.getVerb(url);
  }
  getSeniorityCompany() {
    const url = `${environment.apiBase}/toolbox/${environment.api_version.toolbox}/laboral/old`;
    return this.commonService.getVerb(url);
  }

  getCountriesRequest() {
    const url = `${environment.apiBase}/toolbox/${environment.api_version.toolbox}/countries/all`;
    this.http
      .get<Array<CountryModel>>(url, {
        params: new CustomHttpParams(true),
        context: new HttpContext().set(IS_PUBLIC_API, true),
      })
      .pipe(
        map((resp) => {
          resp.sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          });
          const indexEsp = resp.findIndex((e) => e.isoCode3 === 'ESP');
          const esp = resp[indexEsp];
          resp.splice(indexEsp, 1);
          resp.unshift(esp);
          return resp;
        }),
      )
      .subscribe((data) => this.countries$.next(data));
  }

  public getCountries() {
    return this.countries$;
  }

  findCountry(isoCode3: string) {
    let county: CountryModel;
    if (this.countries$ && this.countries$.getValue()) {
      county = this.countries$.getValue().find((e) => e.isoCode3 === isoCode3);
    }

    return county ? county.name : isoCode3;
  }
}
